import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { useLocation, useParams } from "react-router-dom";
import Webcam from "react-webcam";
import { message, Alert, Modal, Spin, Flex } from "antd";
import ShareCallServices from "../Services/ShareCallServices";
import { convertUTCDate } from "../utils/convertionFunc";
import AppointmentService from "../Services/AppointmentService";
import { decryptData } from "../utils/valueDecrypt";

function ShareCall(props) {
  const params = useParams();
  const [name, setName] = useState("");
  const [userName, setUserName] = useState();
  const [apptDetails, setApptDetails] = useState(null);
  const location = useLocation();
  const [param, setParam] = useState();
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState({
    prescription: false,
    medicalHistory: false,
    reports: false,
    vitals: false,
  });

  const [mediaState, setMediaState] = useState("checking");
  const [mediaError, setMediaError] = useState("");
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [isJoining, setIsJoining] = useState(false);

  const permissionStreamRef = useRef(null);

  const isMediaSupported = useCallback(() => {
    return (
      typeof window !== "undefined" &&
      window.isSecureContext &&
      !!navigator.mediaDevices &&
      typeof navigator.mediaDevices.getUserMedia === "function"
    );
  }, []);

  const stopPermissionStream = useCallback(() => {
    if (!permissionStreamRef.current) return;

    permissionStreamRef.current.getTracks().forEach((track) => {
      try {
        track.stop();
      } catch (e) {
        console.warn("Failed to stop media track:", e);
      }
    });

    permissionStreamRef.current = null;
  }, []);

  const getPermissionState = useCallback(async () => {
    if (!navigator.permissions?.query) {
      return {
        camera: "unknown",
        microphone: "unknown",
      };
    }

    let camera = "unknown";
    let microphone = "unknown";

    try {
      const result = await navigator.permissions.query({
        name: "camera",
      });
      camera = result.state;
    } catch (e) {
      console.debug("Camera permission query unsupported:", e);
    }

    try {
      const result = await navigator.permissions.query({
        name: "microphone",
      });
      microphone = result.state;
    } catch (e) {
      console.debug("Microphone permission query unsupported:", e);
    }

    return { camera, microphone };
  }, []);

  const requestMediaPermissions = useCallback(async () => {
    setMediaError("");

    if (!isMediaSupported()) {
      setMediaState("unsupported");

      setMediaError(
        "This browser does not support camera and microphone access, " +
          "or the page is not running over HTTPS.",
      );

      return false;
    }

    try {
      setMediaState("requesting");

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "user",
        },
        audio: true,
      });

      permissionStreamRef.current = stream;

      setMediaState("granted");
      setMediaError("");

      stopPermissionStream();

      return true;
    } catch (error) {
      console.error("Camera/microphone permission error:", error);

      stopPermissionStream();

      const errorName = error?.name;

      if (
        errorName === "NotAllowedError" ||
        errorName === "PermissionDeniedError"
      ) {
        setMediaState("denied");

        setMediaError(
          "Camera or microphone access is blocked for this site. " +
            "Allow Camera and Microphone in your browser site settings, " +
            "then press Retry.",
        );

        setShowPermissionModal(true);

        return false;
      }

      if (errorName === "NotFoundError") {
        setMediaState("not-found");
        setMediaError("No camera or microphone was found on this device.");
        return false;
      }

      if (errorName === "NotReadableError") {
        setMediaState("busy");
        setMediaError(
          "The camera or microphone is already being used by another app or tab.",
        );
        return false;
      }

      if (errorName === "SecurityError") {
        setMediaState("blocked");
        setMediaError(
          "The browser blocked access to your camera or microphone.",
        );
        return false;
      }

      setMediaState("error");
      setMediaError(
        `Unable to access camera/microphone${
          error?.message ? `: ${error.message}` : "."
        }`,
      );

      return false;
    }
  }, [isMediaSupported, stopPermissionStream]);

  const checkMediaPermissions = useCallback(async () => {
    if (!isMediaSupported()) {
      setMediaState("unsupported");
      return;
    }

    const permissions = await getPermissionState();

    console.log("Media permission state:", permissions);

    if (
      permissions.camera === "granted" &&
      permissions.microphone === "granted"
    ) {
      setMediaState("granted");
      return;
    }

    if (
      permissions.camera === "denied" ||
      permissions.microphone === "denied"
    ) {
      setMediaState("denied");

      setMediaError(
        "Camera and microphone are blocked for this site. " +
          "Allow them in browser site settings.",
      );

      return;
    }

    setMediaState("prompt");
  }, [getPermissionState, isMediaSupported]);

  useEffect(() => {
    checkMediaPermissions();
  }, [checkMediaPermissions]);

  const handleEnableMedia = async () => {
    const granted = await requestMediaPermissions();

    if (granted) {
      setShowPermissionModal(false);
    }
  };

  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState !== "visible") return;

      await checkMediaPermissions();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    window.addEventListener("focus", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);

      window.removeEventListener("focus", handleVisibilityChange);
    };
  }, [checkMediaPermissions]);

  const getCodeFromURL = () => {
    const query = window.location.search;
    const codeMatch = query.match(/ref=([^/]+)/);
    const extraMatch = query.match(/ref=[^/]+\/([^&]*)/);
    const code = codeMatch ? codeMatch[1] : null;
    const MedicalShare = extraMatch ? extraMatch[1] : null;
    return { code, MedicalShare };
  };

  useEffect(() => {
    let param = getCodeFromURL();
    setParam(param);
    if (param?.code) {
      ShareCallServices.getUserName(param?.code)
        .then((res) => {
          setUserName(res.data[0].userName);
        })
        .catch((error) => {
          console.error(error);
        });
    }
  }, []);

  const save = async (e) => {
    e.preventDefault();
    if (name.trim() === "") {
      message.error("Please enter the name");
      return;
    }

    if (mediaState !== "granted") {
      const granted = await requestMediaPermissions();
      if (!granted) {
        return;
      }
    }

    setIsJoining(true);
    const currentDate = new Date();
    const formattedDate = convertUTCDate(currentDate, "YYYY-MM-DD HH:mm:ss");
    let param = getCodeFromURL();

    try {
      let payload1 = {
        personName: name,
        personType: "Others",
        appointmentId: param?.code,
        createdDate: formattedDate,
        modifiedDate: formattedDate,
        joinDateTime: formattedDate,
        status: 1,
      };
      await AppointmentService.shareCallJoiningHisUpdate(payload1);
    } catch (e) {
      console.error(e);
    }

    const payload = {
      createdDate: formattedDate,
      merchantId: "hslA/69EPSD7qXTYAqWxuCqnq8KLo8Q3AFgvFVKsIBk=",
      merchantUserId: "HELIXO",
      modifiedDate: formattedDate,
      saltkey: "ojKdN1zB/MdTtJ1hBoIzVtRK60oyos5x29koJaadxL8=",
      uniqueId: param?.code,
      userName: name,
    };

    try {
      const response = await axios.post(
        "https://api.v-connect.helixotech.com/HelixoConnect/join-call",
        payload,
      );
      const decryptedString = decryptData(response.data.data);
      const decryptedJson =
        typeof decryptedString === "string"
          ? JSON.parse(decryptedString)
          : decryptedString;

      if (decryptedJson?.url) {
        window.location.href = decryptedJson.url;
      } else {
        message.error("Failed to get call link");
        setIsJoining(false);
      }
    } catch (err) {
      console.error("Video joining error:", err);
      message.error("Could not join the video call");
      setIsJoining(false);
    }
  };

  const onChangeHandler = (fieldName, value) => {
    if (fieldName === "name") {
      setName(value);
    }
  };

  const handleOpenModal = () => {
    setModalIsOpen(true);
  };

  const handleCloseModal = () => {
    setModalIsOpen(false);
  };

  const handleCheckboxChange = (event) => {
    const { name, checked } = event.target;
    setSelectedOptions((prevState) => ({
      ...prevState,
      [name]: checked,
    }));
  };

  const handleFormSubmit = (event) => {
    event.preventDefault();
    let payload = {
      appointmentId: param?.code,
      medicalHistory: selectedOptions.medicalHistory ? "Y" : "N",
      prescription: selectedOptions.prescription ? "Y" : "N",
      reports: selectedOptions.reports ? "Y" : "N",
      vitals: selectedOptions.vitals ? "Y" : "N",
      status: 1,
    };
    ShareCallServices.updateMedicalRecords(payload)
      .then((response) => {
        message.success("Updated successfully");
        handleCloseModal();
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const videoConstraints = {
    width: 1280,
    height: 720,
    facingMode: "user",
  };

  const shouldShowPermissionGate = mediaState !== "granted";

  return (
    <div className="flex flex-col items-center justify-center min-h-[100dvh] bg-sky-300 p-4">
      {shouldShowPermissionGate ? (
        <div className="bg-white p-6 rounded-lg shadow-2xl w-full max-w-md">
          {mediaState === "checking" && (
            <div className="py-12">
              <Flex align="center" justify="center" gap="middle">
                <Spin size="large" tip="Checking camera and microphone..." />
              </Flex>
            </div>
          )}

          {(mediaState === "prompt" || mediaState === "requesting") && (
            <div className="text-center py-4">
              <h2 className="text-xl font-bold mb-3 text-center">
                Camera & Microphone Access Required
              </h2>
              <p className="text-gray-600 mb-6 text-sm">
                Please allow your camera and microphone so you can join the
                consultation.
              </p>
              <div className="flex flex-col gap-3">
                <button
                  onClick={handleEnableMedia}
                  disabled={mediaState === "requesting"}
                  className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-semibold transition-colors disabled:opacity-50"
                >
                  {mediaState === "requesting"
                    ? "Requesting Access..."
                    : "Enable Camera & Microphone"}
                </button>
                <button
                  onClick={() => (window.location.href = "https://doxzon.com")}
                  className="w-full py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md font-medium text-sm transition-colors"
                >
                  Leave
                </button>
              </div>
            </div>
          )}

          {(mediaState === "denied" || mediaState === "blocked") && (
            <div className="text-center py-4">
              <h2 className="text-xl font-bold mb-3 text-red-600">
                Camera & Microphone Blocked
              </h2>
              <p className="mb-4 text-gray-600 text-sm">
                Your browser has blocked camera or microphone access for this site.
              </p>
              <Alert
                type="warning"
                showIcon
                message="Allow camera and microphone in your browser/site settings."
                description="After changing the permission, return to this page and press Retry."
                className="text-left mb-5"
              />
              <div className="flex gap-3">
                <button
                  onClick={() => (window.location.href = "https://doxzon.com")}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md w-1/2 font-medium"
                >
                  Leave
                </button>
                <button
                  onClick={handleEnableMedia}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md w-1/2 font-medium"
                >
                  Retry
                </button>
              </div>
            </div>
          )}

          {mediaState === "unsupported" && (
            <div className="text-center py-4">
              <h2 className="text-xl font-bold mb-3 text-red-600">
                Browser Not Supported
              </h2>
              <p className="text-gray-600 mb-6 text-sm">
                Please open this consultation using a browser that supports
                camera and microphone access and make sure the page is served over HTTPS.
              </p>
              <button
                onClick={() => (window.location.href = "https://doxzon.com")}
                className="w-full py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-md font-medium"
              >
                Leave
              </button>
            </div>
          )}

          {mediaState === "not-found" && (
            <div className="text-center py-4">
              <h2 className="text-xl font-bold mb-3 text-amber-600">
                Camera or Microphone Not Found
              </h2>
              <p className="text-gray-600 mb-6 text-sm">{mediaError}</p>
              <div className="flex gap-3">
                <button
                  onClick={() => (window.location.href = "https://doxzon.com")}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md w-1/2 font-medium"
                >
                  Leave
                </button>
                <button
                  onClick={handleEnableMedia}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md w-1/2 font-medium"
                >
                  Retry
                </button>
              </div>
            </div>
          )}

          {mediaState === "busy" && (
            <div className="text-center py-4">
              <h2 className="text-xl font-bold mb-3 text-amber-600">
                Camera or Microphone Busy
              </h2>
              <p className="text-gray-600 mb-6 text-sm">{mediaError}</p>
              <div className="flex gap-3">
                <button
                  onClick={() => (window.location.href = "https://doxzon.com")}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md w-1/2 font-medium"
                >
                  Leave
                </button>
                <button
                  onClick={handleEnableMedia}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md w-1/2 font-medium"
                >
                  Retry
                </button>
              </div>
            </div>
          )}

          {mediaState === "error" && (
            <div className="text-center py-4">
              <h2 className="text-xl font-bold mb-3 text-red-600">
                Unable to Access Camera/Microphone
              </h2>
              <p className="text-gray-600 mb-6 text-sm">{mediaError}</p>
              <div className="flex gap-3">
                <button
                  onClick={() => (window.location.href = "https://doxzon.com")}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md w-1/2 font-medium"
                >
                  Leave
                </button>
                <button
                  onClick={handleEnableMedia}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md w-1/2 font-medium"
                >
                  Retry
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-white p-6 rounded-lg shadow-2xl w-full max-w-md">
          <div>
            <h2 className="text-xl font-bold mb-2 text-center">
              Are you ready to join?
            </h2>
            <h6 className="text-center font-medium">
              Hi, Kindly choose a quiet, comfortable place with good internet
              connectivity before your consultation begins
            </h6>
            {param?.MedicalShare == "Y" && (
              <>
                <h6 className="text-center font-medium mt-2 text-gray-500">
                  Please share your medical records by clicking the below button.
                </h6>
                <div className="flex justify-center">
                  <button
                    className="text-center mb-4 cursor-pointer font-semibold mt-3 border px-2 py-2 rounded-lg bg-blue-600 text-white"
                    onClick={handleOpenModal}
                  >
                    Share Medical Records
                  </button>
                </div>
              </>
            )}
            <div className="flex gap-5 mt-2 justify-center">
              <input
                type="text"
                value={name}
                onChange={(e) => {
                  onChangeHandler("name", e.target.value);
                }}
                className="border p-2 rounded-lg w-80 outline-none"
                placeholder="Enter Name"
              />
            </div>
          </div>
          <div className="flex justify-center mb-4 mt-3">
            <Webcam
              audio={false}
              screenshotFormat="image/jpeg"
              className="rounded-lg w-full object-cover h-36 sm:h-52 md:h-60"
              videoConstraints={videoConstraints}
              onUserMediaError={(err) =>
                console.warn("Webcam preview error:", err)
              }
            />
          </div>

          {userName ? (
            <p className="text-center mb-4 font-bold">
              Consultation with {userName}
            </p>
          ) : null}
          <div className="flex justify-between gap-3">
            <button
              onClick={() => (window.location.href = "https://doxzon.com")}
              className="px-4 py-2 bg-red-600 text-white rounded-md w-1/2"
            >
              Leave
            </button>
            <button
              onClick={(e) => save(e)}
              disabled={isJoining}
              className="px-4 py-2 bg-blue-600 text-white rounded-md w-1/2 disabled:opacity-50"
            >
              {isJoining ? "Joining..." : "Join now"}
            </button>
          </div>
        </div>
      )}

      {modalIsOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded-lg shadow-2xl w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Share Medical Records</h2>
            <form onSubmit={handleFormSubmit}>
              <div className="mb-2">
                <input
                  type="checkbox"
                  name="prescription"
                  checked={selectedOptions.prescription}
                  onChange={handleCheckboxChange}
                />
                <label className="ml-2 cursor-pointer font-medium">
                  Prescription
                </label>
              </div>
              <div className="mb-2">
                <input
                  type="checkbox"
                  name="medicalHistory"
                  checked={selectedOptions.medicalHistory}
                  onChange={handleCheckboxChange}
                />
                <label className="ml-2 cursor-pointer font-medium">
                  Medical History
                </label>
              </div>
              <div className="mb-2">
                <input
                  type="checkbox"
                  name="vitals"
                  checked={selectedOptions.vitals}
                  onChange={handleCheckboxChange}
                />
                <label className="ml-2 cursor-pointer font-medium">
                  Vitals
                </label>
              </div>
              <div className="mb-2">
                <input
                  type="checkbox"
                  name="reports"
                  checked={selectedOptions.reports}
                  onChange={handleCheckboxChange}
                />
                <label className="ml-2 cursor-pointer font-medium">
                  Reports
                </label>
              </div>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 bg-red-500 text-white rounded-md"
                >
                  Close
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md mr-2"
                >
                  Submit
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <Modal
        open={showPermissionModal}
        title="Camera & Microphone Permission Required"
        footer={
          <button
            onClick={handleEnableMedia}
            className="border px-4 py-2 bg-blue-600 text-white rounded-md"
          >
            Retry
          </button>
        }
        onCancel={() => setShowPermissionModal(false)}
      >
        <p>Your browser has blocked access to the camera or microphone.</p>
        <p className="mt-3">
          Open the browser's site permissions, change Camera and Microphone
          from <strong>Block</strong> to <strong>Allow</strong>, then return
          here and press Retry.
        </p>
      </Modal>
    </div>
  );
}

export default ShareCall;
